import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { Icon } from "@iconify/react";
import HeaderBreadcrumbs from "../../core/components/HeaderBreadcrumbs";
import { Img } from "../../components/styled";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getAccessContext,
  getRecordById,
  removeSecretRecord,
  retryJob,
  retryMeetingSummary,
  submitAccessRequest,
  type RecordAccessContext,
} from "../../services/record.service";
import ReactMarkdown from "react-markdown";
import { useCallback, useEffect, useRef, useState } from "react";
import useIsMountedRef from "../../hooks/useIsMountedRef";
import { AlignmentType, Document, Packer, Paragraph, TextRun } from "docx";
import { buildDownloadFileName } from "../../utils/downloadFileName";
import { generateTranscriptPdf } from "../../utils/pdfExport";
import { handleSecretRecordAfterDownload } from "../../utils/secretRecordDownload";
import { getMsalToken } from "../../contexts/AzureEntraContext";
import { getEnvironmentVariable } from "../../utils/env";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import {
  AccessRequestPanel,
  AccessDeniedPanel,
  OwnerAccessRequestsPanel,
} from "../../components/access/AccessRequestPanel";
import {
  RecordAccessInviteCard,
  useRecordAccessActions,
} from "../../components/access/RecordAccessManagement";
import { PATH_DASHBOARD } from "../../routes/paths";
import RecordDeleteDialog from "../../components/records/RecordDeleteDialog";
import SpeakerAssignmentCard from "../../components/speakers/SpeakerAssignmentCard";
import { loadTranscriptForExport } from "../../utils/transcriptExport";

const SUMMARY_PENDING_STALE_RETRY_MS = 5 * 60 * 1000;

function formatEngineLabel(engine?: string | null) {
  if (!engine) return "—";
  if (engine === "faster_whisper") return "Whisper";
  if (engine === "speechmatics") return "Boardnote";
  return engine;
}

function formatFileSize(fileSize?: string | number | null): string {
  if (fileSize == null || fileSize === "") return "—";
  const bytes = typeof fileSize === "number" ? fileSize : Number(fileSize);
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  const digits = value >= 10 || exponent === 0 ? 0 : 1;

  return `${value.toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })} ${units[exponent]}`;
}

function isRecordOwnedByEmail(record: any, userEmail?: string | null): boolean {
  if (!userEmail || !record?.user?.email) return false;
  return record.user.email.toLowerCase() === userEmail.toLowerCase();
}

function MetaField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" component="span">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

export default function FileDetail() {
  const [recordInfo, setRecordInfo] = useState<any>();
  const [accessContext, setAccessContext] = useState<RecordAccessContext | null>(null);
  const [submittingAccessRequest, setSubmittingAccessRequest] = useState(false);
  const [retrySummaryLoading, setRetrySummaryLoading] = useState(false);
  const [retryJobLoading, setRetryJobLoading] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<"forbidden" | "not_found" | "unknown" | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const reviewRequestId = searchParams.get("review_request");
  const highlightRequestId = reviewRequestId ? Number(reviewRequestId) : null;
  const recordId = params?.id ? Number(params.id) : null;
  const { enqueueSnackbar } = useSnackbar();
  const { companyInfo } = useAuth();
  const isMountedRef = useIsMountedRef();
  const accessRequestsPanelRef = useRef<HTMLDivElement>(null);
  const secretDownloadInFlightRef = useRef(false);
  const _currentUserEmail = companyInfo?.user_email ?? "";
  const isRecordOwner = isRecordOwnedByEmail(recordInfo, _currentUserEmail);
  const access = useRecordAccessActions(recordId, isRecordOwner);
  const canDownloadTranscript = recordInfo?.status === "TRANSCRIPTED";

  const beginSecretDownload = (): boolean => {
    if (!recordInfo?.is_secret) return true;
    if (secretDownloadInFlightRef.current) {
      enqueueSnackbar("Gizli kayıt yalnızca bir kez indirilebilir.", { variant: "warning" });
      return false;
    }
    secretDownloadInFlightRef.current = true;
    return true;
  };

  const endSecretDownload = () => {
    if (recordInfo?.is_secret) {
      secretDownloadInFlightRef.current = false;
    }
  };

  const finalizeSecretDownload = async (jobId: string | number) => {
    await handleSecretRecordAfterDownload(jobId, recordInfo?.is_secret, {
      enqueueSnackbar,
      onRemoved: async () => {
        if (params?.id) await getRecordDetail(params.id);
      },
    });
  };

  const handleApproveRequest = async (requestId: number) => {
    await access.handleApproveRequest(requestId);
    if (params?.id) await getRecordDetail(params.id);
  };

  const resolveSummaryState = (info: any): string => {
    if (info?.summary_status_user) return info.summary_status_user;
    const raw = info?.summary_status;
    if (!raw) return "None";
    if (raw === "InProgress") return "Processing";
    if (raw === "RetryRequested") return "Pending";
    return raw;
  };

  const getProcessingMessage = (info: any): string => {
    if (info?.processing_phase === "Summarizing") return "Yapay zeka özeti hazırlanıyor...";
    if (info?.processing_phase === "Diarizing") return "Konuşmacılar ayrıştırılıyor...";
    return "Transkripsiyon yapılıyor...";
  };

  const canRetryPreparingSummary = (info: any): boolean => {
    const state = resolveSummaryState(info);
    if (state !== "Pending" && state !== "Processing") return false;
    if (!info?.summary_updated_at) return false;
    const updatedAt = new Date(info.summary_updated_at).getTime();
    if (!Number.isFinite(updatedAt)) return false;
    return Date.now() - updatedAt >= SUMMARY_PENDING_STALE_RETRY_MS;
  };

  const getRecordDetail = async (recordId: any, options?: { initial?: boolean }) => {
    try {
      const response = await getRecordById(recordId);
      if (isMountedRef.current && response?.status === 200) {
        setRecordInfo(response?.data?.data);
        setAccessContext(null);
        setPageError(null);
      }
      return response;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403 && inviteToken) {
        await loadAccessContext(Number(recordId), inviteToken, options?.initial === true);
        return null;
      }
      if (options?.initial && isMountedRef.current) {
        if (status === 403) setPageError("forbidden");
        else if (status === 404) setPageError("not_found");
        else setPageError("unknown");
      }
      return null;
    }
  };

  const loadAccessContext = async (recordId: number, token: string, initial = false) => {
    try {
      const response = await getAccessContext(recordId, token);
      if (!isMountedRef.current || response?.status !== 200) {
        if (initial) setPageError("forbidden");
        return;
      }
      const ctx = response.data?.data as RecordAccessContext;
      setAccessContext(ctx);
      setPageError(null);
      if (ctx.access_state === "granted") {
        const detail = await getRecordById(recordId);
        if (isMountedRef.current && detail?.status === 200) {
          setRecordInfo(detail.data?.data);
          setAccessContext(null);
        }
      }
    } catch (error: any) {
      console.error(error);
      if (initial && isMountedRef.current) {
        setPageError(error?.response?.status === 404 ? "not_found" : "forbidden");
      } else {
        enqueueSnackbar("Erişim bilgisi alınamadı.", { variant: "error" });
      }
    }
  };

  const handleSubmitAccessRequest = async (message?: string) => {
    if (!params?.id || !inviteToken) return;
    setSubmittingAccessRequest(true);
    try {
      const response = await submitAccessRequest(Number(params.id), inviteToken, message);
      if (response?.status === 200) {
        enqueueSnackbar("Erişim isteğiniz gönderildi.", { variant: "success" });
        await loadAccessContext(Number(params.id), inviteToken);
      }
    } catch {
      enqueueSnackbar("Erişim isteği gönderilemedi.", { variant: "error" });
    } finally {
      setSubmittingAccessRequest(false);
    }
  };

  const handleRetryMeetingSummary = async () => {
    if (!params?.id) return;
    setRetrySummaryLoading(true);
    try {
      await retryMeetingSummary(Number(params.id));
      await getRecordDetail(params.id);
      enqueueSnackbar("Toplantı özeti yeniden kuyruğa alındı.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Özet yeniden denenirken hata oluştu.", { variant: "error" });
    } finally {
      setRetrySummaryLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!recordInfo?.job_id) return;
    setDeleteLoading(true);
    try {
      const response = await removeSecretRecord(recordInfo.job_id);
      if (response?.status === 200) {
        enqueueSnackbar("Kayıt başarıyla silindi.", { variant: "success" });
        setDeleteDialogOpen(false);
        navigate(PATH_DASHBOARD.general.app);
        return;
      }
      enqueueSnackbar("Kayıt silinemedi.", { variant: "error" });
    } catch {
      enqueueSnackbar("Kayıt silinemedi.", { variant: "error" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRetryJob = async () => {
    if (!params?.id) return;
    setRetryJobLoading(true);
    try {
      await retryJob(Number(params.id));
      await getRecordDetail(params.id);
      enqueueSnackbar("İş yeniden kuyruğa alındı.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Yeniden denenirken hata oluştu.", { variant: "error" });
    } finally {
      setRetryJobLoading(false);
    }
  };

  /**
   * Fetch the transcript payload for a record using the streaming endpoint.
   * The server writes raw JSON directly to the response body — no double-encoding.
   * Using fetch (not axios) so the browser reads from a ReadableStream and only
   * allocates the parsed object once, rather than buffering a giant string first.
   */
  const fetchRichPayload = async (jobId: string) => loadTranscriptForExport(recordInfo.id, async () => {
    const token = await getMsalToken();
    const apiUrl = getEnvironmentVariable("REACT_APP_API_URL");
    const res = await fetch(`${apiUrl}/webhooks/record_result_rich?recordId=${encodeURIComponent(jobId)}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return res.json();
  });

  /**
   * Extract the ordered speaker-group array from the richResult object.
   * richResult arrives as a parsed object (never a string) from the streaming endpoint.
   * Shape: { speaker_groups: SpeakerGroup[] } | SpeakerGroup[] | null
   */
  const parseSpeakerGroups = (richResult: any): any[] => {
    if (!richResult) return [];
    if (Array.isArray(richResult)) return richResult;
    if (Array.isArray(richResult?.speaker_groups)) return richResult.speaker_groups;
    return [];
  };

  /**
   * Derive plain-text fallback from richResult.speaker_groups.
   * Used when a download needs a string and richResult is present but groups is empty.
   * Mirrors FormatTranscriptionBySpeakers on the server side.
   * Returns "" when richResult is null — callers must then fall back to hashedResult.
   */
  const buildPlainTextFromRich = (richResult: any, speakerNames: ReadonlyMap<string, string>): string => {
    const groups = parseSpeakerGroups(richResult);
    if (groups.length === 0) return "";
    return groups
      .map((g: any) => {
        let line = "";
        g.words?.forEach((w: any) => {
          if (w.type === "word") line += (line ? " " : "") + w.text;
          else if (w.type === "punctuation") line += w.text;
        });
        const name = resolveSpeakerName(g.speaker, speakerNames);
        return `[${name}]:\n${line}`;
      })
      .join("\n\n");
  };

  /**
   * Resolve a speaker label to its assigned display name.
   * Falls back to the raw label if no assignment exists.
   */
  const resolveSpeakerName = (label: string, speakerNames: ReadonlyMap<string, string>): string => {
    return speakerNames.get(label) || label;
  };

  // Shared helper: richResult speaker groups → plain text
  const buildSpeakerText = (groups: any[], speakerNames: ReadonlyMap<string, string>): string => {
    return groups
      .map((g: any) => {
        let line = "";
        g.words?.forEach((w: any) => {
          if (w.type === "word") line += (line ? " " : "") + w.text;
          else if (w.type === "punctuation") line += w.text;
        });
        const name = resolveSpeakerName(g.speaker, speakerNames);
        return `[${name}]:\n${line}`;
      })
      .join("\n\n");
  };

  // Shared helper: richResult speaker groups → docx Paragraph[] (no color)
  const buildSpeakerParagraphs = (groups: any[], speakerNames: ReadonlyMap<string, string>): any[] => {
    const pars: any[] = [];
    groups.forEach((g: any) => {
      const name = resolveSpeakerName(g.speaker, speakerNames);
      pars.push(
        new Paragraph({ children: [new TextRun({ text: `[${name}]:`, bold: true })] })
      );
      let text = "";
      g.words?.forEach((w: any) => {
        if (w.type === "word") text += (text ? " " : "") + w.text;
        else if (w.type === "punctuation") text += w.text;
      });
      if (text) pars.push(new Paragraph({ alignment: AlignmentType.BOTH, children: [new TextRun(text)] }));
    });
    return pars;
  };

  const speakersDirtyRef = useRef(false);
  const handleSpeakersDirtyChange = useCallback((dirty: boolean) => {
    speakersDirtyRef.current = dirty;
  }, []);
  const confirmUnsavedSpeakers = () =>
    !speakersDirtyRef.current ||
    window.confirm("Konuşmacı atamalarında kaydedilmemiş değişiklikler var. Bu değişiklikler dosyaya yansımayacak. Yine de indirmek istiyor musunuz?");

  const downloadTxtFile = async (jobId: any) => {
    if (!confirmUnsavedSpeakers()) return;
    if (!beginSecretDownload()) return;
    try {
      const payload = await fetchRichPayload(String(jobId));
      const groups = parseSpeakerGroups(payload.richResult);
      let paragraphs: any[];
      if (groups.length > 0) {
        paragraphs = buildSpeakerParagraphs(groups, payload.speakerNames);
      } else {
        enqueueSnackbar("Rich transcript parse edilemedi, düz transkript kullanıldı.", { variant: "warning" });
        // Legacy record: richResult is null, fall back to hashedResult plain text.
        const fallback = buildPlainTextFromRich(payload.richResult, payload.speakerNames) || payload.hashedResult || "";
        paragraphs = fallback
          .split("\n")
          .map((item: string) => new Paragraph({ alignment: AlignmentType.BOTH, children: [new TextRun(item)] }));
      }
      const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });
      const blob = await Packer.toBlob(doc);
      const element = document.createElement("a");
      element.href = URL.createObjectURL(blob);
      element.download = buildDownloadFileName(recordInfo?.record_name, recordInfo?.created_date);
      document.body.appendChild(element);
      element.click();
      await finalizeSecretDownload(jobId);
    } catch (error) {
      enqueueSnackbar("Dosya indirilirken bir hata meydana geldi: " + error);
      console.error(error);
    } finally {
      endSecretDownload();
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    const threshold = parseFloat(process.env.REACT_APP_CONFIDENCE_THRESHOLD || "0.65");
    if (confidence >= threshold) return "000000"; // black
    return "F44336"; // red
  };

  const downloadColoredDocx = async (jobId: any) => {
    if (!confirmUnsavedSpeakers()) return;
    if (!beginSecretDownload()) return;
    try {
      const payload = await fetchRichPayload(String(jobId));
      const parsedData = parseSpeakerGroups(payload.richResult);

      if (parsedData.length === 0) {
        enqueueSnackbar("Renkli DOCX için richResult verisi mevcut değil");
        return;
      }

      const paragraphs: any[] = [];
      parsedData.forEach((speakerGroup: any) => {
        const name = resolveSpeakerName(speakerGroup.speaker, payload.speakerNames);
        paragraphs.push(
          new Paragraph({ children: [new TextRun({ text: `[${name}]:`, bold: true })] }),
        );
        const items: { text: string; color: string }[] = [];
        speakerGroup.words?.forEach((word: any) => {
          if (word.type === "word") {
            items.push({ text: word.text, color: getConfidenceColor(word.confidence) });
          } else if (word.type === "punctuation") {
            if (items.length > 0) items[items.length - 1].text += word.text;
            else items.push({ text: word.text, color: "000000" });
          }
        });
        const runs = items.map((item, i) =>
          new TextRun({ text: i > 0 ? ` ${item.text}` : item.text, color: item.color }),
        );
        if (runs.length > 0)
          paragraphs.push(new Paragraph({ alignment: AlignmentType.BOTH, children: runs }));
      });

      const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });
      const blob = await Packer.toBlob(doc);
      const element = document.createElement("a");
      element.href = URL.createObjectURL(blob);
      element.download = buildDownloadFileName(recordInfo?.record_name, recordInfo?.created_date, {
        suffix: "renkli",
      });
      document.body.appendChild(element);
      element.click();
      await finalizeSecretDownload(jobId);
    } catch (error) {
      enqueueSnackbar("Dosya indirilirken bir hata meydana geldi: " + error);
      console.error(error);
    } finally {
      endSecretDownload();
    }
  };

  const downloadTxt = async (jobId: any) => {
    if (!confirmUnsavedSpeakers()) return;
    if (!beginSecretDownload()) return;
    try {
      const payload = await fetchRichPayload(String(jobId));
      const groups = parseSpeakerGroups(payload.richResult);
      if (groups.length === 0) {
        enqueueSnackbar("Rich transcript parse edilemedi, düz transkript kullanıldı.", { variant: "warning" });
      }
      const content = groups.length > 0
        ? buildSpeakerText(groups, payload.speakerNames)
        // Legacy record: richResult null — use hashedResult plain text.
        : (buildPlainTextFromRich(payload.richResult, payload.speakerNames) || payload.hashedResult || "");
      const element = document.createElement("a");
      element.href = URL.createObjectURL(new Blob([content], { type: "text/plain;charset=utf-8" }));
      element.download = buildDownloadFileName(recordInfo?.record_name, recordInfo?.created_date, {
        extension: "txt",
      });
      document.body.appendChild(element);
      element.click();
      await finalizeSecretDownload(jobId);
    } catch (error) {
      enqueueSnackbar("Dosya indirilirken bir hata meydana geldi: " + error);
      console.error(error);
    } finally {
      endSecretDownload();
    }
  };

  const downloadPdf = async (jobId: any) => {
    if (!confirmUnsavedSpeakers()) return;
    if (!beginSecretDownload()) return;
    try {
      const payload = await fetchRichPayload(String(jobId));
      const groups = parseSpeakerGroups(payload.richResult);
      if (groups.length === 0) {
        enqueueSnackbar("Rich transcript parse edilemedi, düz transkript kullanıldı.", { variant: "warning" });
      }
      const plainText = groups.length > 0
        ? undefined
        : (buildPlainTextFromRich(payload.richResult, payload.speakerNames) || payload.hashedResult || "");

      await generateTranscriptPdf({
        groups,
        plainText,
        speakerNames: payload.speakerNames,
        fileName: buildDownloadFileName(recordInfo?.record_name, recordInfo?.created_date, {
          extension: "pdf",
        }),
      });
      await finalizeSecretDownload(jobId);
    } catch (error) {
      enqueueSnackbar("PDF indirilirken bir hata meydana geldi: " + error);
      console.error(error);
    } finally {
      endSecretDownload();
    }
  };

  useEffect(() => {
    if (!params?.id) return;
    const recordId = Number(params.id);
    let cancelled = false;

    void (async () => {
      setPageLoading(true);
      setPageError(null);
      const detail = await getRecordDetail(recordId, { initial: true });
      if (cancelled) return;
      // Owner access requests / invite status load via useRecordAccessActions when isRecordOwner.
      if (!cancelled) setPageLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id, inviteToken]);

  useEffect(() => {
    const pendingCount = access.accessRequests.filter((r) => r.status === "Pending").length;
    if (!reviewRequestId || pendingCount === 0) return;
    const timer = window.setTimeout(() => {
      accessRequestsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [reviewRequestId, access.accessRequests]);

  useEffect(() => {
    if (!params?.id) return;
    const id = params.id;

    // Poll while transcribing (batch in progress) — covers all phases
    if (recordInfo?.status === "TRANSCRIBING") {
      const t = window.setInterval(() => { void getRecordDetail(id); }, 4000);
      return () => window.clearInterval(t);
    }

    if (recordInfo?.status !== "TRANSCRIPTED") return;
    const status = recordInfo?.summary_status;
    const userStatus = recordInfo?.summary_status_user;
    const isSummaryProcessing =
      status === "Pending" ||
      status === "RetryRequested" ||
      status === "InProgress" ||
      userStatus === "Pending" ||
      userStatus === "Processing";
    if (!isSummaryProcessing) return;

    const t = window.setInterval(() => {
      void getRecordDetail(id);
    }, 5000);
    return () => window.clearInterval(t);
    // processing_phase intentionally excluded — a phase change alone doesn't
    // change whether we should poll; it would only reset the interval timer.
  }, [params?.id, recordInfo?.status, recordInfo?.summary_status, recordInfo?.summary_status_user]);



  const fileSizeLabel = formatFileSize(recordInfo?.file_size);
  const _sharedWithEmails: string[] = recordInfo?.shared_with_emails ?? [];
  const isSharedWithCurrentUser =
    !!_currentUserEmail &&
    recordInfo?.user?.email !== _currentUserEmail &&
    (recordInfo?.shared_with_email === _currentUserEmail ||
      _sharedWithEmails.includes(_currentUserEmail));

  const createdLabel =
    recordInfo?.created_date != null
      ? new Date(recordInfo.created_date).toLocaleString("tr-TR", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "—";
  const summaryState = resolveSummaryState(recordInfo);
  const showPreparingSummaryRetry = canRetryPreparingSummary(recordInfo);
  const pendingAccessRequests = access.accessRequests.filter((r) => r.status === "Pending");
  const hasReviewRequestParam =
    highlightRequestId != null && Number.isFinite(highlightRequestId);
  const showAccessFallback =
    !pageLoading && !recordInfo && !accessContext && pageError != null;

  return (
    <Container>
      <HeaderBreadcrumbs
        heading={recordInfo?.record_name ?? accessContext?.record_name ?? "Dosya Detay"}
        links={[{ name: "Dashboard", href: "/" }, { name: "Dosya Detay" }]}
      />

      {pageLoading && !recordInfo && !accessContext ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {showAccessFallback ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: { xs: "flex-start", md: "center" },
            minHeight: { xs: "auto", md: "calc(100vh - 220px)" },
            width: "100%",
            py: { xs: 3, md: 0 },
            px: 2,
          }}
        >
          <AccessDeniedPanel error={pageError ?? "forbidden"} />
        </Box>
      ) : null}

      {!recordInfo && accessContext && inviteToken ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: { xs: "flex-start", md: "center" },
            minHeight: { xs: "auto", md: "calc(100vh - 220px)" },
            width: "100%",
            py: { xs: 3, md: 0 },
            px: 2,
          }}
        >
          <AccessRequestPanel
            context={accessContext}
            inviteToken={inviteToken}
            submitting={submittingAccessRequest}
            onSubmit={handleSubmitAccessRequest}
          />
        </Box>
      ) : null}

      {recordInfo && pendingAccessRequests.length > 0 ? (
        <Box ref={accessRequestsPanelRef} id="access-requests-panel">
          <OwnerAccessRequestsPanel
            requests={access.accessRequests}
            highlightRequestId={Number.isFinite(highlightRequestId) ? highlightRequestId : null}
            actingId={access.actingRequestId}
            bulkActing={access.bulkActing}
            onApprove={handleApproveRequest}
            onReject={access.handleRejectRequest}
            onBulkApprove={access.handleBulkApprove}
            onBulkReject={access.handleBulkReject}
          />
        </Box>
      ) : null}

      {recordInfo &&
      access.accessRequestsLoaded &&
      hasReviewRequestParam &&
      pendingAccessRequests.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Bu erişim isteği bulunamadı veya zaten yanıtlandı.{" "}
          <Link
            component={RouterLink}
            to={
              recordId
                ? PATH_DASHBOARD.reports.auditDetail(recordId)
                : PATH_DASHBOARD.reports.audit
            }
          >
            Erişim denetiminde görüntüleyin
          </Link>
          .
        </Alert>
      ) : null}

      {recordInfo && isRecordOwner && access.inviteStatus ? (
        <RecordAccessInviteCard
          recordId={recordId}
          inviteStatus={access.inviteStatus}
          inviteActionLoading={access.inviteActionLoading}
          confirmDialog={access.confirmDialog}
          onSetConfirmDialog={access.setConfirmDialog}
          onConfirmInviteAction={access.handleConfirmInviteAction}
        />
      ) : null}

      {recordInfo && (
        <Card sx={{ p: 3 }}>
          <Stack spacing={{ xs: 2, md: 3 }}>
            {recordInfo.is_secret && (
              <Alert
                severity="warning"
                icon={<Icon icon="mdi:lock-outline" width={22} />}
              >
                Bu kayıt gizlidir. Transkript yalnızca bir kez indirilebilir; indirme
                sonrası sunucudan silinir.
              </Alert>
            )}

            <MetaField label="Dosya İsmi">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body1">{recordInfo.record_name}</Typography>
                {isSharedWithCurrentUser ? (
                  <Tooltip title="Sizinle paylaşıldı">
                    <Chip
                      icon={<Icon icon="mdi:share-variant-outline" width={16} />}
                      label="Shared with you"
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            </MetaField>

            <MetaField label="Oluşturulma">
              <Typography variant="body1">{createdLabel}</Typography>
            </MetaField>

            <MetaField label="Çeviri motoru">
              <Typography variant="body1">
                {formatEngineLabel(recordInfo.engine)}
              </Typography>
            </MetaField>

            {recordInfo.attendees && recordInfo.attendees.length > 0 && (
              <>
                <Divider />
                <MetaField label="Katılımcılar">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                        lg: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {recordInfo.attendees.map((a: any, i: number) => (
                      <Stack
                        key={i}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ minWidth: 0 }}
                      >
                        <Icon icon="mdi:account-outline" width={18} />
                        <Typography variant="body2" sx={{ minWidth: 0 }}>
                          {a.name}
                        </Typography>
                        {a.email && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                          >
                            ({a.email})
                          </Typography>
                        )}
                        {a.role && a.role !== "participant" && (
                          <Chip label={a.role} size="small" variant="outlined" />
                        )}
                      </Stack>
                    ))}
                  </Box>
                </MetaField>
              </>
            )}

            {/* ── Speaker Assignment Card (only for TRANSCRIPTED records with speaker labels) ── */}
            {recordInfo.status === "TRANSCRIPTED" && (
              <>
                <Divider />
                <SpeakerAssignmentCard
                  key={recordInfo.id}
                  recordId={recordInfo.id}
                  canEdit={isRecordOwner}
                  onDirtyChange={handleSpeakersDirtyChange}
                />
              </>
            )}

            {/* ── Transcription-phase progress & failure ── */}
            {recordInfo.status === "TRANSCRIBING" && (
              <>
                <Divider />
                <MetaField label="İşlem durumu">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {getProcessingMessage(recordInfo)}
                    </Typography>
                  </Stack>
                </MetaField>
              </>
            )}

            {recordInfo.status === "FAILED" && (
              <>
                <Divider />
                <MetaField label="İşlem durumu">
                  <Stack spacing={1}>
                    <Alert severity="error">
                      {recordInfo.failed_step
                        ? `İşlem başarısız oldu (adım: ${recordInfo.failed_step}).`
                        : "İşlem başarısız oldu."}
                    </Alert>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={retryJobLoading}
                      onClick={handleRetryJob}
                      startIcon={retryJobLoading ? <CircularProgress size={16} /> : undefined}
                    >
                      Yeniden dene
                    </Button>
                  </Stack>
                </MetaField>
              </>
            )}

            {recordInfo.auto_summarize && !recordInfo.is_secret && (
              <>
                <Divider />
                <MetaField label="Yapay zeka özeti">
                  <Stack spacing={1.5} sx={{ width: "100%" }}>
                    {(summaryState === "Processing" || summaryState === "Pending") && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          {retrySummaryLoading
                            ? "Özet yeniden deneniyor..."
                            : showPreparingSummaryRetry
                            ? "Özet beklemede kaldı."
                            : recordInfo.processing_phase === "Summarizing"
                            ? "Özetleniyor..."
                            : "Özet hazırlanıyor..."}
                        </Typography>
                        {showPreparingSummaryRetry && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={retrySummaryLoading}
                            onClick={handleRetryMeetingSummary}
                            startIcon={
                              retrySummaryLoading ? (
                                <CircularProgress size={14} />
                              ) : undefined
                            }
                          >
                            Özeti yeniden dene
                          </Button>
                        )}
                      </Stack>
                    )}

                    {summaryState === "Ready" && !!recordInfo.summary_text && (
                      <Box
                        sx={{
                          "& h1": { fontSize: "1.5rem", mt: 0, mb: 1 },
                          "& h2": { fontSize: "1.2rem", mt: 2, mb: 1 },
                          "& ul": { pl: 2, my: 1 },
                          "& p": { my: 0.5 },
                        }}
                      >
                        <ReactMarkdown>{recordInfo.summary_text}</ReactMarkdown>
                      </Box>
                    )}

                    {summaryState === "Ready" && !recordInfo.summary_text && (
                      <Typography variant="body2" color="text.secondary">
                        Özet metni yok.
                      </Typography>
                    )}



                    {(summaryState === "Failed" || summaryState === "ContentFiltered") && (
                      <Stack spacing={1}>
                        {recordInfo.summary_error ? (
                          <Alert severity="error">{recordInfo.summary_error}</Alert>
                        ) : (
                          <Alert severity="error">Özet oluşturulamadı.</Alert>
                        )}
                        <Button
                          variant="outlined"
                          color="primary"
                          disabled={retrySummaryLoading}
                          onClick={handleRetryMeetingSummary}
                          startIcon={retrySummaryLoading ? <CircularProgress size={16} /> : undefined}
                        >
                          Özeti yeniden dene
                        </Button>
                      </Stack>
                    )}

                    {summaryState === "Skipped" && (
                      <Typography variant="body2" color="text.secondary">
                        Bu kayıt için özet atlandı.
                      </Typography>
                    )}
                  </Stack>
                </MetaField>
              </>
            )}

            <Divider />

            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm="auto">
                <Box
                  sx={{
                    maxWidth: { xs: 120, sm: 96 },
                    mx: { xs: "auto", sm: 0 },
                  }}
                >
                  <Img
                    alt=""
                    src="/illustrations/file.png"
                    sx={{ width: "100%", height: "auto", display: "block" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm>
                <Stack spacing={1}>
                  <MetaField label="Dosya adı">
                    <Typography variant="body1">
                      Toplantı Çevirisi
                    </Typography>
                  </MetaField>
                  <MetaField label="Boyut">
                    <Typography variant="body1">
                      {fileSizeLabel}
                    </Typography>
                  </MetaField>
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            {canDownloadTranscript && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Transkripti indir (job: {recordInfo.job_id})
                </Typography>
                <Tooltip title="Transkripti indir - Formatı Seçin">
                  <IconButton
                    onClick={(e) => setDownloadAnchor(e.currentTarget)}
                    color="primary"
                    aria-label="Transkripti indir"
                    sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}
                  >
                    <Icon icon="akar-icons:download" width={22} />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={downloadAnchor}
                  open={Boolean(downloadAnchor)}
                  onClose={() => setDownloadAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      downloadColoredDocx(recordInfo.job_id);
                      setDownloadAnchor(null);
                    }}
                  >
                    DOCX (Renkli)
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      downloadTxtFile(recordInfo.job_id);
                      setDownloadAnchor(null);
                    }}
                  >
                    DOCX (Düz Metin)
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      downloadTxt(recordInfo.job_id);
                      setDownloadAnchor(null);
                    }}
                  >
                    TXT
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      downloadPdf(recordInfo.job_id);
                      setDownloadAnchor(null);
                    }}
                  >
                    PDF
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {isRecordOwner && recordInfo.status !== "DELETED" ? (
              <>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "stretch", sm: "flex-end" },
                    width: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Icon icon="mdi:delete-outline" width={20} />}
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Kaydı Sil
                  </Button>
                </Box>
              </>
            ) : null}
          </Stack>
        </Card>
      )}

      <RecordDeleteDialog
        open={deleteDialogOpen}
        loading={deleteLoading}
        recordName={recordInfo?.record_name}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteRecord}
      />
    </Container>
  );
}
